import React, { useCallback, useMemo, useState } from 'react';
import { DataTable } from '../../components/organisms';
import { Button } from '../../components/atoms';
import { DeleteConfirmationModal } from '../../components/organisms/DeleteConfirmationModal';
import { SplashScreenFormModal } from '../../components/organisms/SplashScreenFormModal';
import { useSplashScreens, useSplashScreenActions } from '../../hooks/useSplashScreens';
import type { Column, TableAction } from '../../types/table';
import type {
  SplashScreen,
  SplashScreenCreateRequest,
  SplashScreenUpdateRequest,
} from '../../types/api/splash-screen';
import { formatDateForDisplay } from '../../types/api/drama';
import { formatTargetPlatform } from '../../types/api/splash-screen';
import { resolveAssetUrl } from '../../utils/url';
import toast from 'react-hot-toast';

/**
 * SplashScreenManagementPage
 *
 * Admin page for managing splash screens:
 * - Server-side pagination and filters (isActive, targetPlatform, language)
 * - CRUD: create (with image or URL), edit (with optional new image), delete
 * - Toggle active via update
 */
export const SplashScreenManagementPage: React.FC = () => {
  const { fetchSplashScreens } = useSplashScreens();
  const {
    createWithImage,
    createWithJson,
    updateWithImage,
    updateWithJson,
    deleteSplashScreen,
  } = useSplashScreenActions();

  const [refreshKey, setRefreshKey] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSplashScreen, setSelectedSplashScreen] = useState<SplashScreen | null>(null);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedSplashScreen(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback((row: SplashScreen) => {
    setSelectedSplashScreen(row);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((row: SplashScreen) => {
    setSelectedSplashScreen(row);
    setIsDeleteModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (row: SplashScreen) => {
      try {
        const loadingToast = toast.loading(row.isActive ? 'Deactivating...' : 'Activating...');
        await updateWithJson(row.splashId, { isActive: !row.isActive });
        toast.dismiss(loadingToast);
        handleRefresh();
      } catch {
        // Error already shown by hook
      }
    },
    [updateWithJson, handleRefresh]
  );

  const handleCreateSubmit = useCallback(
    async (
      data: SplashScreenCreateRequest | SplashScreenUpdateRequest,
      imageFile?: File | null
    ) => {
      try {
        if (imageFile) {
          await createWithImage(data as SplashScreenCreateRequest, imageFile);
        } else {
          await createWithJson(data as SplashScreenCreateRequest);
        }
        setIsCreateModalOpen(false);
        handleRefresh();
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to create splash screen';
        toast.error(message);
      }
    },
    [createWithImage, createWithJson, handleRefresh]
  );

  const handleEditSubmit = useCallback(
    async (
      data: SplashScreenCreateRequest | SplashScreenUpdateRequest,
      imageFile?: File | null
    ) => {
      if (!selectedSplashScreen) return;
      try {
        if (imageFile) {
          await updateWithImage(selectedSplashScreen.splashId, data as SplashScreenUpdateRequest, imageFile);
        } else {
          await updateWithJson(selectedSplashScreen.splashId, data as SplashScreenUpdateRequest);
        }
        setIsEditModalOpen(false);
        setSelectedSplashScreen(null);
        handleRefresh();
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to update splash screen';
        toast.error(message);
      }
    },
    [selectedSplashScreen, updateWithImage, updateWithJson, handleRefresh]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedSplashScreen) return;
    try {
      await deleteSplashScreen(selectedSplashScreen.splashId);
      setIsDeleteModalOpen(false);
      setSelectedSplashScreen(null);
      handleRefresh();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete splash screen';
      toast.error(message);
    }
  }, [selectedSplashScreen, deleteSplashScreen, handleRefresh]);

  const columns = useMemo<Column<SplashScreen>[]>(
    () => [
      {
        id: 'splash',
        header: 'Splash Screen',
        accessor: 'title',
        cell: (row) => (
          <div className="flex items-center gap-3">
            <div className="w-20 h-12 rounded bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {row.imageUrl ? (
                <img
                  src={resolveAssetUrl(row.imageUrl) ?? row.imageUrl}
                  alt={row.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <span className="text-xs font-semibold text-gray-600">No image</span>
              )}
            </div>
            <div className="min-w-0 text-left">
              <p className="text-sm font-semibold text-gray-900 truncate">{row.title}</p>
              {row.description && (
                <p className="text-xs text-gray-500 truncate max-w-[200px]">{row.description}</p>
              )}
            </div>
          </div>
        ),
        minWidth: '250px',
      },
      {
        id: 'sequenceOrder',
        header: 'Order',
        accessor: 'sequenceOrder',
        cell: (row) => (
          <span className="text-sm text-gray-600">{row.sequenceOrder}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'status',
        header: 'Status',
        accessor: 'isActive',
        cell: (row) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              row.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            {row.isActive ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        id: 'targetPlatform',
        header: 'Platform',
        accessor: 'targetPlatform',
        cell: (row) => (
          <span className="text-sm text-gray-600">{formatTargetPlatform(row.targetPlatform)}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'language',
        header: 'Language',
        accessor: 'language',
        cell: (row) => (
          <span className="text-sm text-gray-600">{row.language}</span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'startDate',
        header: 'Start',
        accessor: 'startDate',
        cell: (row) => (
          <span className="text-sm text-gray-600 text-left">
            {row.startDate ? formatDateForDisplay(row.startDate) : '—'}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'endDate',
        header: 'End',
        accessor: 'endDate',
        cell: (row) => (
          <span className="text-sm text-gray-600 text-left">
            {row.endDate ? formatDateForDisplay(row.endDate) : '—'}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessor: 'createdAt',
        cell: (row) => (
          <span className="text-sm text-gray-600 text-left">
            {formatDateForDisplay(row.createdAt)}
          </span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  const actions = useMemo<TableAction<SplashScreen>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'toggle-status',
        label: 'Toggle Status',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: (row) => handleToggleActive(row),
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleToggleActive, handleDelete]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Splash Screen Management</h1>
          <p className="text-gray-600 mt-1 pl-3">
            Manage onboarding and welcome splash screens
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleCreate}
            leftIcon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            }
          >
            Add Splash Screen
          </Button>
        </div>
      </div>

      <DataTable<SplashScreen>
        columns={columns}
        fetchData={fetchSplashScreens}
        initialPageSize={20}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Splash screen management table"
        key={refreshKey}
        renderHeaderActions={({ setFilter, removeFilter, filters }) => {
          const isActiveFilter = filters.find((f) => f.columnId === 'isActive');
          const currentIsActive = isActiveFilter?.value ?? '';
          const targetPlatformFilter = filters.find((f) => f.columnId === 'targetPlatform');
          const currentPlatform = targetPlatformFilter?.value ?? '';
          const languageFilter = filters.find((f) => f.columnId === 'language');
          const currentLanguage = languageFilter?.value ?? '';

          return (
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="splash-is-active-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Status:
              </label>
              <select
                id="splash-is-active-filter"
                value={currentIsActive}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') removeFilter('isActive');
                  else setFilter({ columnId: 'isActive', operator: 'equals', value });
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
              <label htmlFor="splash-platform-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap ml-2">
                Platform:
              </label>
              <select
                id="splash-platform-filter"
                value={currentPlatform}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') removeFilter('targetPlatform');
                  else setFilter({ columnId: 'targetPlatform', operator: 'equals', value });
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
              >
                <option value="">All</option>
                <option value="all">All</option>
                <option value="android">Android</option>
                <option value="ios">iOS</option>
              </select>
              <label htmlFor="splash-language-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap ml-2">
                Language:
              </label>
              <select
                id="splash-language-filter"
                value={currentLanguage}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') removeFilter('language');
                  else setFilter({ columnId: 'language', operator: 'equals', value });
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#EAB308]/30 focus:border-[#EAB308]"
              >
                <option value="">All</option>
                <option value="en">English</option>
                <option value="my">Myanmar</option>
              </select>
            </div>
          );
        }}
      />

      <SplashScreenFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        mode="create"
      />

      <SplashScreenFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedSplashScreen(null);
        }}
        onSubmit={handleEditSubmit}
        mode="edit"
        splashScreen={selectedSplashScreen ?? undefined}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedSplashScreen(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Splash Screen"
        message={
          selectedSplashScreen
            ? `Are you sure you want to delete "${selectedSplashScreen.title}"? This action cannot be undone.`
            : 'Are you sure you want to delete this splash screen?'
        }
      />
    </div>
  );
};

export default SplashScreenManagementPage;
